<template>
  <v-container class="mt-2">
    <v-row class="mb-12">
      <v-col cols="12">
        <v-row>
          <v-col cols="12" sm="8" lg="9" xl="10" class="pb-0">
            <h1 class="text-h4 font-weight-bold mb-2">Curators</h1>
            <h2 class="text-subtitle-1">
              There are no rules for good photographs, there are only good
              photographs - Ansel Adams
            </h2>
          </v-col>
          <v-col cols="12" sm="4" lg="3" xl="2" align-self="end" class="py-0">
            <v-text-field
              v-model="searchCuratorValue"
              v-debounce:300ms="searchCurator"
              :loading="loading"
              label="Search Curator"
              prepend-inner-icon="fal fa-search fa-sm"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-for="(curator, index) in curators"
        :key="index"
        cols="12"
        md="6"
        xl="4"
      >
        <v-card outlined class="mb-1 stretch">
          <nuxt-link
            :to="`/photographer/${curator.userName || curator.id}`"
            nuxt
          >
            <v-img
              v-if="curator.bestUserPhoto"
              :src="$asa.getImageUrl(curator.bestUserPhoto, 'thumb')"
              :lazy-src="$asa.getImageUrl(curator.bestUserPhoto, 'thumb', true)"
              :height="300"
            ></v-img>
            <div v-else class="tiles"></div>
          </nuxt-link>
          <v-card-title primary-title>
            <div class="text-center w-100-percentage hero-overlap">
              <nuxt-link
                :to="`/photographer/${curator.userName || curator.id}`"
                nuxt
              >
                <v-avatar size="120px">
                  <img
                    :src="curator.avatarUrl"
                    :alt="`${curator.fullName} Avatar`"
                    :class="
                      $vuetify.theme.dark ? 'black-border' : 'white-border'
                    "
                  />
                </v-avatar>
              </nuxt-link>
              <nuxt-link
                :to="`/photographer/${curator.userName || curator.id}`"
                class="no-underline"
                nuxt
              >
                <h3 class="mt-4 text-h6 font-weight-bold text-truncate mb-2">
                  {{ curator.fullName }}
                </h3>
              </nuxt-link>
              <h4 class="text-subtitle-2 text-uppercase font-weight-light">
                <span v-if="curator.country" class="mr-1 va-top">
                  <img
                    :src="`https://www.flagcdn.com/h20/${curator.country.toLowerCase()}.png`"
                    :srcset="`https://flagcdn.com/h20/${curator.country.toLowerCase()}.png 2x`"
                  />
                </span>
                {{ curator.role
                }}{{ curator.isCritic ? ' &amp; CRITIC' : '' }}
              </h4>
              <p class="text-body-2 mt-4" v-html="curator.about" />
              <div>
                <ul class="list-inline">
                  <li v-if="curator.website">
                    <v-btn
                      :href="`https://${curator.website}`"
                      target="_blank"
                      icon
                      ><v-icon small>fal fa-globe</v-icon></v-btn
                    >
                  </li>
                  <li v-if="curator.facebook">
                    <v-btn
                      :href="`https://www.facebook.com/${curator.facebook}`"
                      icon
                      target="_blank"
                      ><v-icon small>fab fa-facebook-f</v-icon></v-btn
                    >
                  </li>
                  <li v-if="curator.twitter">
                    <v-btn
                      :href="`https://twitter.com/${curator.twitter}`"
                      icon
                      target="_blank"
                      ><v-icon small>fab fa-twitter</v-icon></v-btn
                    >
                  </li>
                  <li v-if="curator.instagram">
                    <v-btn
                      :href="`https://www.instagram.com/${curator.instagram}`"
                      icon
                      target="_blank"
                      ><v-icon small>fab fa-instagram</v-icon></v-btn
                    >
                  </li>
                  <li v-if="curator.portfolioUrl && curator.isPortfolioActive">
                    <v-btn
                      :href="`${portfolioDomain}/${curator.portfolioUrl}`"
                      icon
                      target="_blank"
                      ><v-icon small>fal fa-briefcase</v-icon></v-btn
                    >
                  </li>
                </ul>
              </div>
            </div>
          </v-card-title>
          <v-card-actions>
            <div class="text-center w-100-percentage">
              <v-btn
                :to="`/photographer/${curator.userName || curator.id}`"
                nuxt
                depressed
                small
                dark
                class="mx-1 mb-4"
                >profile</v-btn
              >
              <v-btn
                v-if="!me || (me && me.id !== curator.id)"
                :dark="!curator.isFollowing"
                :color="curator.isFollowing ? 'tertiary' : 'bluezone'"
                :loading="loadingFollow"
                depressed
                small
                class="mx-1 mb-4"
                @click="followUser(curator.id, curator.isFollowing)"
                >{{ curator.isFollowing ? 'unfollow' : 'follow' }}</v-btn
              >
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <div v-intersect="onIntersect" />
    <div v-if="loading" class="text-center">
      <v-icon class="fa-spin my-3"> fal fa-spinner </v-icon>
    </div>
    <div
      v-if="!loading && curators.length === 0 && mounted"
      class="text-center w-100-percentage mt-12"
    >
      <div>
        <v-icon class="d-block mb-3">far fa-sad-cry</v-icon>
        No curators found for this search
      </div>
    </div>
  </v-container>
</template>

<script>

import { mapState } from 'vuex'
import { EventBus } from '@/plugins/event-bus'

export default {
  data() {
    return {
      loading: false,
      loadingFollow: false,
      searchCuratorValue: '',
      mounted: false,
      portfolioDomain: process.env.portfolioDomain
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Curators',
      description:
        "Find out the list of 100ASA's curators. Areas of expertise. Short bio, prizes, achievements. Top Profiles. Follow buttons."
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      curators: state => state.users.curators,
      curatorsIsLastPage: state => state.users.curatorsIsLastPage,
      curatorsPage: state => state.users.curatorsPage
    })
  },
  async mounted() {
    this.mounted = true
    if (this.$store.state.users.curators.length === 0) {
      try {
        this.loading = true
        await this.$store.dispatch('users/getCurators', {
          page: 1
        })
      } catch (error) {
        this.$asa.appInsights('Pages: curators.vue - method: mounted', error)
      } finally {
        this.loading = false
      }
    }
  },
  methods: {
    onIntersect(entries) {
      if (
        entries[0].intersectionRatio > 0 &&
        this.curators.length > 0 &&
        !this.curatorsIsLastPage &&
        !this.loading
      ) {
        this.loadMore()
      }
    },
    async loadMore() {
      try {
        this.loading = true
        const page = this.curatorsPage + 1
        await this.$store.dispatch('users/getCurators', {
          page
        })
      } catch (error) {
        this.$asa.appInsights('Pages: curators.vue - method: loadMore', error)
      } finally {
        this.loading = false
      }
    },
    async followUser(id, following) {
      if (this.me) {
        try {
          this.loadingFollow = true
          await this.$store.dispatch(
            `following/${following ? 'unfollow' : 'follow'}`,
            {
              userId: id
            }
          )
          await this.$store.dispatch('feeds/resetStore')
          await this.$auth.fetchUser()
        } catch (error) {
          this.$asa.appInsights('Pages: curators.vue - method: followUser', error)
        } finally {
          this.loadingFollow = false
        }
      } else {
        EventBus.$emit('snackbar', {
          text: 'You need to log in to follow a Curator',
          color: 'info'
        })
      }
    },
    async searchCurator(value) {
      try {
        this.loading = true
        await this.$store.dispatch('users/resetStore')
        await this.$store.dispatch('users/getCurators', {
          searchText: value
        })
      } catch (error) {
        this.$asa.appInsights('Pages: curators.vue - method: searchCurator', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>

.tiles {
  background: url('/images/curators/triangles.svg') 0 0 no-repeat transparent;
  background-size: cover;
  height: 300px;
}
.hero-overlap {
  margin-top: -75px;
}
.white-border {
  border: 2px solid #fafafa;
}
.black-border {
  border: 2px solid #212121;
}
.stretch {
  height: 100%;
}
</style>
